import { useState } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import { Topbar, Sidebar, AccountIndex, ApplicationIndex, CreditIndex, FilesIndex, Loader, BreadCrump, LoanSettings, LoanState, Flag, FlagBanner } from "../../components";
import { useLocation } from "react-router-dom";
import { useLoan } from '../../hooks';
import EvaluationPanel from '../Loan/Evaluation/EvaluationPanel';
import useConfigOptions from "../../hooks/useConfigOptions";
import { HugeiconsIcon } from '@hugeicons/react';
import { Flag01Icon } from '@hugeicons/core-free-icons';

function Loan() {

    const { loanStates } = useConfigOptions();
    const location = useLocation();
    const { pathname } = location;
    const path = pathname.split("/");
    const loanState = path[1];
    const loanId = path[2];

    const [isModalOpen, setIsModalOpen] = useState(false);
    const isFlagModalOpen = () => setIsModalOpen(!isModalOpen);
    const isFlagModalClosed = () => setIsModalOpen(!isModalOpen);

    const { isLoading, loan } = useLoan(
        { loanId, loanType: null, state: loanState, approved: false, active: loanState === loanStates.active, completed: loanState === loanStates.completed, rejected: loanState === loanStates.rejected }
    );

    const breadcrumbPath =
        loanState === loanStates.active    ? "/active-loans" :
        loanState === loanStates.completed ? "/completed-loans" :
        "/rejected-loans";

    const breadcrumbMain =
        loanState === loanStates.active    ? "Active Loans" :
        loanState === loanStates.completed ? "Completed Loans" :
        "Rejected Loans";

    const tabs = ["Loan Account", "Application", "Evaluation", "Credit Bureau", "Files"];

    return (
        <div className='main-container'>
            <Topbar />
            <Sidebar />

            <main className='main-content'>
                <section className='section-container space-y-5'>

                {loan ?
                    <>
                    <div className="flex items-center justify-between">
                        <BreadCrump
                            path={breadcrumbPath}
                            main={breadcrumbMain}
                            sub={isLoading ? <Loader color={"text-gray-700"} size={"w-4 h-4 ml-1"} /> : `${loan?.type} — ${loan?._id}`}
                        />

                        {!isLoading && (
                            <div className="flex items-center gap-2">
                                <button
                                    type='button'
                                    onClick={isFlagModalOpen}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-500 border border-red-100 dark:border-red-600/20 rounded-lg bg-red-50/60 dark:bg-red-500/20 hover:bg-red-50 transition-colors"
                                >
                                    <HugeiconsIcon icon={Flag01Icon} size={14} />
                                    Flag
                                </button>
                                <LoanSettings loan={loan} />
                            </div>
                        )}
                    </div>

                    {loan?.clientFlagged && Object.keys(loan.clientFlagged).length > 0 && (
                        <FlagBanner clientFlagged={loan.clientFlagged} />
                    )}

                    {loanState === loanStates.active &&
                        <LoanState loan={loan} />
                    }

                    <TabGroup>
                        <TabList className="flex border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
                            {tabs.map((label) => (
                                <Tab
                                    key={label}
                                    className={({ selected }) =>
                                        `shrink-0 px-6 py-3.5 text-sm font-medium border-b-2 -mb-px transition-colors focus:outline-none whitespace-nowrap ${
                                            selected
                                                ? 'border-[#1e3a6e] text-[#1e3a6e] dark:border-blue-400 dark:text-blue-400'
                                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                                        }`
                                    }
                                >
                                    {label}
                                </Tab>
                            ))}
                        </TabList>

                        <TabPanels className="mt-0">
                            <TabPanel><AccountIndex loan={loan} /></TabPanel>
                            <TabPanel><ApplicationIndex loan={loan} /></TabPanel>
                            <TabPanel><EvaluationPanel loan={loan} /></TabPanel>
                            <TabPanel><CreditIndex loan={loan} /></TabPanel>
                            <TabPanel><FilesIndex loan={loan} /></TabPanel>
                        </TabPanels>
                    </TabGroup>
                    </>
                :
                <div className='w-full py-12 inline-flex justify-center items-center'>
                    <Loader color={"text-gray-500"} size={"w-6 h-6"} />
                </div>
                }
                </section>
            </main>

            <Flag
                isOpen={isModalOpen}
                isClosed={isFlagModalClosed}
                client={loan?.client}
            />
        </div>
    );
}

export default Loan;
